import React, {useContext} from 'react';
import './style.scss';
import {Table, Tag, Popover, Button} from 'antd';
import {epochToDate} from '../../libs/utility';
import {Context} from '../../store';
import { UpCircleOutlined, DownCircleOutlined, CloseCircleOutlined, StarFilled, PercentageOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';

const Last24HoursDealsTable = ({data}) => {
    const {state} = useContext(Context);

    const linkPrefix = state.marketType === "FUTURES" ? "futures" : "spot";

    const columns = [
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: text => tagBuilder(text),
          filters: [
              {
                  text: 'LONG',
                  value: 'OPEN LONG'
              },
              {
                  text: 'SHORT',
                  value: 'OPEN SHORT'
              }
          ],
          onFilter: (value, record) => record.status.indexOf(value) === 0
        },
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            key: 'symbol',
            render: (symbol, record) => symbolBuilder(symbol, record),
            sorter: (a, b) => ('' + a.symbol).localeCompare(b.symbol),
        },
        /*
        {
            title: 'Pos Model',
            dataIndex: 'positionModel',
            key: 'positionModel',
            render: text => text
        },
        {
            title: 'Maturity',
            dataIndex: 'maturity',
            key: 'maturity',
            render: text => text
        },
        */
        {
            title: 'Pos Type',
            dataIndex: 'positionType',
            key: 'positionType',
            render: (text, record) => posBuilder(record),
        },
        {
            title: 'Open Price',
            dataIndex: 'openprice',
            key: 'openprice',
            render: price => priceBuilder(price)
        },
        {
            title: 'Current Price',
            dataIndex: 'closeprice',
            key: 'closeprice',
            render: price => `$ ${price}`
          },
        {
          title: 'Start Date',
          key: 'opendate',
          dataIndex: 'opendate',
          render: date => epochToDate(date).toLocaleString()
        },
        {
            title: 'Pass Time',
            key: 'time',
            dataIndex: 'time',
            render: time => `${time} ${time === 1 ? 'hour' : 'hours'}`,
            sorter: (a, b) => a.time - b.time,
        },
        {
            title: 'Reverse (C)',
            key: 'closeReverse',
            dataIndex: 'closeReverse',
            render: (closeReverse, record) => reverseBuilder(closeReverse, record.closeReverseIndex),
            sorter: (a, b) => a.closeReverse - b.closeReverse,
        },
        {
              title: 'Reverse (M)',
              key: 'maxReverse',
              dataIndex: 'maxReverse',
              render: (maxReverse, record) => reverseBuilder(maxReverse, record.maxReverseIndex),
              sorter: (a, b) => a.maxReverse - b.maxReverse,
        },
        {
            title: 'First Reverse',
            key: 'firstReverse',
            dataIndex: 'firstReverse',
            render: (firstReverse, record) => reverseBuilder(firstReverse, 1),
            sorter: (a, b) => a.firstReverse - b.firstReverse,
        },
        {
            title: 'Max Profit',
            key: 'maxProfit',
            dataIndex: 'maxProfit',
            render: (maxProfit, record) => reverseBuilder(maxProfit, record.highpriceIndex),
            sorter: (a, b) => a.maxProfit - b.maxProfit,
        },
        {
            title: 'High Profit',
            key: 'highProfit',
            dataIndex: 'highProfit',
            render: (highProfit, record) => reverseBuilder(highProfit, record.topNeedleIndex),
            sorter: (a, b) => a.highProfit - b.highProfit,
        },
        {
            title: 'Profit/Loss',
            key: 'pnl',
            dataIndex: 'pnl',
            render: (pnl, record) => `${pnl} %`,
            sorter: (a, b) => a.pnl - b.pnl,
        }
      ];

    const tagBuilder = (text) => {
        let color = null;
        let icon = null;
        switch (text) {
            case 'LONG':
                color = "#87d068";
                icon = <UpCircleOutlined />;
                break;
            case 'SHORT':
                color = "#f50";
                icon = <DownCircleOutlined />;
                break;
            case 'CLOSE POSITION':
                color = "#3b5081";
                icon = <CloseCircleOutlined />;
                break;
            default:
                color = "black";
                break;
        }
        return <div className='deals-tag'>
                    <Tag icon={icon} color={color}>{text.toUpperCase()}</Tag>
               </div>
    }

    const symbolBuilder = (symbol, record) => {
        const imageName = state.marketType === "FUTURES" ? state.symbols.find(x => x.symbol === symbol)?.baseAsset?.toLowerCase() : 'binance';
        return <div className="symbol">
            <Link to={`/${linkPrefix}-currencies/${symbol.replace('USDT', '').toLowerCase()}`}>
                <img src={`${process.env.PUBLIC_URL}/images/crypto-logo/${imageName}.svg`} alt="" />
                <span><strong>{symbol}</strong></span>
                {/*
                <span className={`risk ${record.riskLevel}`}>
                    {record.riskLevel === "star" && <StarFilled style={{color: '#17a5f9', fontSize: 16}} />}
                </span>                
                */}
            </Link>
        </div>
    }

    const posBuilder = (record) => {
        return <div className="pos-type">
            <Tag color={"#87d068"}>{record.posType}</Tag>
        </div>
    }

    const reverseBuilder = (reverse, index) => {
        return <div className="reverse">
            <span className="reverse-value">{reverse} %</span>
            {index > 0 && <span className="reverse-index">{index}</span>}
        </div>
    }

    const percentageColumns = [
        {
            title: 'Profit',
            dataIndex: 'profitPrice',
            key: 'profitPrice'
        },
        {
            title: '%',
            dataIndex: 'percent',
            key: 'percent'
        },
        {
            title: 'Loss',
            dataIndex: 'lossPrice',
            key: 'lossPrice'
        }
    ];

    const percentTable = (price) => {
        let fixed = price.toString().split('.')[1] || 0;
        const percentData = [];
        const numPrice = Number(price);
        for (let i = 1; i < 6; i++) {
            const obj = {
                percent: i,
                profitPrice: ((numPrice / 100) * i + numPrice).toFixed(fixed.length),
                lossPrice: ((numPrice / 100) * -i + numPrice).toFixed(fixed.length),
            }
            percentData.push(obj);          
        }
        return <Table columns={percentageColumns} dataSource={percentData} bordered pagination={false} />
    }

    const priceBuilder = (price) => {
        return <div className="price">
            <span className='price-text'>$ {price}</span>
            <Popover placement="rightTop" title={"Percentages"} content={percentTable(price)} trigger="click">
                <Button type="primary" size="small" shape="circle" icon={<PercentageOutlined />}></Button>
            </Popover>
        </div>
    }

    return (
        <div className='last-24-hours-deals-table'>
            <Table columns={columns} dataSource={data} bordered pagination={false} rowClassName={
                (record, index) => record.highProfit > 1 ? "soft-green" : "soft-red"
            }/>
        </div>
    );
};


export default Last24HoursDealsTable;